import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import AppBar from 'components/AppBar';
import DeleteButton from 'components/Buttons/DeleteButton';
import FloatingSubmitButton from 'components/Buttons/FloatingSubmitButton';
import Field from 'components/Field';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import FullScreenToolTip, { Item } from 'components/FullScreenToolTip';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import CurrencyInput from 'containers/CurrencyInput';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import ResponseScreen, { BackgroundTypes } from 'screens/components/ResponseScreen';
import { extractErrorMessage, htmlT, formatTrim } from 'utils/generic';

import Authorization from './components/ApprovalMethod';
import GroupCabins from './components/GroupCabins';
import GroupPayments from './components/GroupPayments';
import GroupPolicies from './components/GroupPolicies';
import GroupUsers from './components/GroupUsers';
import { Container, Divider, Form, Fields } from './styles';

type SetFieldValue = (field: string, value: unknown) => void;
type SetFieldTouched = (field: string) => void;

interface Props {
  loading: boolean;
  submitting: boolean;
  group?: Group;
  countryCurrency: string;
  isFromSelection?: boolean;
  originRule?: string;
  onConfirm: (group: Group) => Promise<void>;
  onCloseFormGroup?: () => void;
  onDelete: (userGroupId: string) => void;
}

const GroupForm = ({
  loading,
  submitting,
  group,
  countryCurrency,
  onConfirm,
  isFromSelection,
  onCloseFormGroup,
  originRule,
  onDelete,
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const toast = useToast();
  const [showSelectUser, setShowSelectUsers] = useState(false);
  const [showSelectRule, setShowSelectRule] = useState(false);
  const [fullScreenTooltip, setFullScreenTooltip] = useState(false);
  const [showPaymentCard, setShowPaymentCard] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [domesticCabinTypes, setDomesticCabinTypes] = useState<string[]>(group?.domesticCabinTypes ?? []);
  const [internationalCabinTypes, setInternationalCabinTypes] = useState<string[]>(
    group?.internationalCabinTypes ?? [],
  );
  
  const [groupsDirty, setGroupsDirty] = useState(false);

  const isEdit = !!group && group.id !== '';

  const toolTipItems: Item[] = [
    {
      icon: '/corporate/images/calculator-colored.svg',
      text: t('group.tooltip.budget.itemTitle'),
    },
    {
      icon: '/corporate/images/computer-colored.svg',
      text: t('group.tooltip.budget.itemSubtitle'),
    },
  ];

  const validationSchema = yup.object().shape({
    name: yup.string().trim().min(3).max(100).required(),
    users: yup.array(),
    budget: yup.string(),
    rule: yup.object().shape({
      id: yup.string().required(),
      name: yup.string().required(),
    }),
    paymentCard: yup.number(),
    authorizationType: yup.string().required(),
    authorizationPrimaryUsers: yup.object().when('authorizationType', {
      is: (authorizationType) => authorizationType && authorizationType !== 'never',
      then: yup.object().shape({
        authorizationUsers: yup.array().required(),
        requireApproval: yup.string(),
      }),
    }),
    authorizationSecondaryUsers: yup.object().when('authorizationType', {
      is: (authorizationType) => authorizationType && authorizationType !== 'simple' && authorizationType !== 'never',
      then: yup.object().shape({
        authorizationUsers: yup.array().required(),
        requireApproval: yup.string(),
      }),
    }),
  });

  const initialValues = {
    name: group?.name || '',
    budget: group?.budget || '',
    users: group?.users || [],
    rule: {
      id: group?.rule?.id || '',
      name: group?.rule?.name || '',
    },
    paymentCardId: group?.paymentCardId || 0,
    authorizationType: group?.authorizationType || '',

    authorizationPrimaryUsers: {
      authorizationUsers: group?.authorizationPrimaryUsers?.authorizationUsers || [],
      requireApproval: group?.authorizationPrimaryUsers?.requireApproval || 'Always',
    },
    authorizationSecondaryUsers: {
      authorizationUsers: group?.authorizationSecondaryUsers?.authorizationUsers || [],
      requireApproval: group?.authorizationSecondaryUsers?.requireApproval || 'Always',
    },
    currency: countryCurrency,
    domesticCabinTypes: group?.domesticCabinTypes || [],
    internationalCabinTypes: group?.internationalCabinTypes || [],
  };

  const handleConfirm = async (values: typeof initialValues) => {
    if (domesticCabinTypes.length === 0) {
      domesticCabinTypes.push('economy');
    }

    if (internationalCabinTypes.length === 0) {
      internationalCabinTypes.push('economy');
    }

    try {
      await onConfirm({
        ...group,
        ...values,
        domesticCabinTypes: domesticCabinTypes,
        internationalCabinTypes: internationalCabinTypes,
      } as Group);
      if (!isFromSelection) setShowSuccess(true);
    } catch (error) {
      const errorMessage = error.response.data.error;
      toast.add({
        type: 'danger',
        message: errorMessage ? errorMessage : extractErrorMessage(error) || t('defaultError.default'),
      });
    }
  };

  const handleDelete = async (userGroupId: string) => {
    try {
      await onDelete(userGroupId);
    } catch (error) {
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) || t('defaultError.default'),
      });
    }
  };

  const handleConfirmSelectionUser = (users: GroupedListModel[], setFieldValue: SetFieldValue) => {
    setFieldValue(
      'users',
      users.map((user) => ({
        id: user.id,
        name: user.name,
        lastName: user.lastName,
        email: formatTrim(user.email),
      })),
    );
  };

  const handleConfirmSelectionRule = (rule: GroupedListModel, setFieldValue: SetFieldValue) => {
    setFieldValue('rule', { id: rule.id, name: rule.name });
  };

  const handleConfirmSelectionCard = (setFieldValue: SetFieldValue, cardId?: string) => {
    if (typeof cardId !== 'undefined') {
      setFieldValue('paymentCardId', cardId);
    }
  };

  const handleAuthorizationApproval = (value: string, formikFieldName: string, setFieldValue: SetFieldValue) => {
    setFieldValue(formikFieldName, value);
  };

  const handleAuthorizationUsers = async (
    value: GroupedListModel[],
    formikFieldName: string,
    setFieldValue: SetFieldValue,
    setFieldTouched: SetFieldTouched,
  ) => {
    await setFieldValue(formikFieldName, value);
    setFieldTouched(formikFieldName);
  };

  const handleNavigateHistory = () => {
    if (isFromSelection) {
      if (onCloseFormGroup) onCloseFormGroup();
    } else {
      history.push('/corporate/groupList');
    }
  };

  const handleOnToggleCabin = (cabinValue: string, type: 'domestic' | 'international') => {
    if (type === 'domestic') {
      handleDomesticCabins(cabinValue);
    }

    if (type === 'international') {
      handleInternationalCabins(cabinValue);
    }

    setGroupsDirty(true);
  };

  const handleInternationalCabins = (cabinValue: string) => {
    const existentCabin = internationalCabinTypes.find((c) => c === cabinValue);

    if (existentCabin !== null && existentCabin !== undefined) {
      setInternationalCabinTypes(internationalCabinTypes.filter((c) => c !== existentCabin));
    } else {
      const newCabins = [...internationalCabinTypes];
      newCabins.push(cabinValue);
      setInternationalCabinTypes(newCabins);
    }
  };

  const handleDomesticCabins = (cabinValue: string) => {
    const existentCabin = domesticCabinTypes.find((c) => c === cabinValue);

    if (existentCabin !== null && existentCabin !== undefined) {
      setDomesticCabinTypes(domesticCabinTypes.filter((c) => c !== existentCabin));
    } else {
      const newCabins = [...domesticCabinTypes];
      newCabins.push(cabinValue);
      setDomesticCabinTypes(newCabins);
    }
  };

  useEffect(() => {
    if (group?.domesticCabinTypes) {
      setDomesticCabinTypes(group?.domesticCabinTypes);
    }
    if (group?.internationalCabinTypes) {
      setInternationalCabinTypes(group?.internationalCabinTypes);
    }
  }, [group]);

  return (
    <Container>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <h5>{t(!group?.id ? 'group.header.create' : 'group.header.edit')}</h5>
        </AppBar>
      </Mobile>
      {loading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader title={t('userMenu.groupsTitle')} subtitle={t('userMenu.groupsDetail')} />
          </Desktop>

          <Formik
            isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
            initialValues={initialValues as Group}
            validationSchema={validationSchema}
            onSubmit={(values: Group) => handleConfirm(values)}
          >
            {({ dirty, values, setFieldValue, setFieldTouched, errors, submitCount, isSubmitting }: FormikValues) => (
              <>
                <FormikSubmitErrorEffect />

                <Form isEdition={isEdit && dirty}>
                  <Fields>
                    <Section title={t('group.details.title')}>
                      <Field name="name" label={t('group.details.name.label')}>
                        <Input placeholder={t('group.details.name.placeHolder')} />
                      </Field>
                    </Section>
                    <Divider />
                    <GroupUsers
                      users={values.users}
                      groupTarget={values.name}
                      showUserSelection={showSelectUser}
                      onConfirmSelection={(users: GroupedListModel[]) =>
                        handleConfirmSelectionUser(users, setFieldValue)
                      }
                      onCloseModal={() => setShowSelectUsers(false)}
                      onAdd={() => setShowSelectUsers(true)}
                    />
                    <Divider />
                    <GroupCabins
                      domesticCabinTypes={domesticCabinTypes}
                      internationalCabinTypes={internationalCabinTypes}
                      onToggle={handleOnToggleCabin}
                    />
                    <Divider />
                    <GroupPolicies
                      isFromSelection={isFromSelection}
                      originRule={originRule}
                      groupTarget={values.name}
                      rule={values.rule}
                      ruleSelected={values.rule}
                      errors={errors}
                      showRulesSelection={showSelectRule}
                      onConfirmSelection={(rule: GroupedListModel) => handleConfirmSelectionRule(rule, setFieldValue)}
                      onCloseModal={() => setShowSelectRule(false)}
                      onAdd={() => setShowSelectRule(true)}
                    />
                    <Divider />                    
                    <GroupPayments
                      showPaymentSelection={showPaymentCard}
                      groupId={group?.id}
                      isEdit={isEdit}
                      paymentCardId={values.paymentCardId}
                      onConfirmSelection={(cardId?: string) => handleConfirmSelectionCard(setFieldValue, cardId)}
                      onCloseModal={() => setShowPaymentCard(false)}
                      onAdd={() => setShowPaymentCard(true)}
                    />
                    <Divider />
                    <Section
                      title={t('group.approvalMethod.title')}
                      details={htmlT(t, 'group.approvalMethod.details')}
                      joinContent
                    >
                      <Authorization
                        values={values}
                        errors={errors}
                        submitted={submitCount > 0}
                        setFieldValue={setFieldValue}
                        onConfirmAuthorizationApproval={(value: string, formikFieldName: string) =>
                          handleAuthorizationApproval(value, formikFieldName, setFieldValue)
                        }
                        onConfirmAuthorizationUsers={(value: GroupedListModel[], formikFieldName: string) =>
                          handleAuthorizationUsers(value, formikFieldName, setFieldValue, setFieldTouched)
                        }
                      />
                    </Section>
                    <Divider />
                    <Section title={t('group.budget.title')} details={htmlT(t, 'group.budget.details')}>
                      <Field
                        tooltip
                        tooltipClick={() => setFullScreenTooltip(true)}
                        name="budget"
                        label={t('group.budget.value.label')}
                      >
                        <CurrencyInput
                          countryCurrency={countryCurrency}
                          placeHolder={t('group.budget.value.placeHolder')}
                        />
                      </Field>
                      <FullScreenToolTip
                        show={fullScreenTooltip}
                        onClose={() => setFullScreenTooltip(false)}
                        title={t('group.tooltip.budget.title')}
                        items={toolTipItems}
                      />
                    </Section>
                  </Fields>
                  <FloatingSubmitButton
                    isEdit={isEdit}
                    dirty={dirty || groupsDirty}
                    loading={submitting || isSubmitting}
                  >
                    {t(isEdit ? 'group.buttons.edit' : 'group.buttons.confirm')}
                  </FloatingSubmitButton>
                  {isEdit && (
                    <>
                      <br />
                      <br />
                      <br />
                      <DeleteButton
                        value={t('group.buttons.delete')}
                        onClick={() => handleDelete(group?.id ?? '')}
                        isEdition={true}
                      />
                    </>
                  )}
                </Form>
              </>
            )}
          </Formik>

          <ResponseScreen
            show={showSuccess}
            background={BackgroundTypes.Mountain}
            text={t('groupSuccess.title')}
            subText={!!group?.id ? t('groupSuccess.textEdit') : t('groupSuccess.text')}
            buttonOnClick={() => history.push('/corporate/groupList')}
            buttonText={t('groupSuccess.buttonText')}
          />
        </>
      )}
    </Container>
  );
};

export default GroupForm;
